// ============================================================================
// Valeurs du Python du lycée (refonte lot 5.7, moteur v28).
// ============================================================================
// Les nombres sont EXACTS : un entier est un BigInt, un « flottant » un rationnel
// (`Rat`) — le sens mathématique de l'écriture décimale, pas le double machine :
// 0.1 + 0.2 == 0.3 est vrai ici. C'est ce qu'un élève calcule à la main en traçant.
// ============================================================================

import type { Stmt } from './ast';
import * as Q from '../exact';
import type { Rat } from '../exact';

export type PyValue =
  | { t: 'int'; v: bigint }
  /** `bin` : une valeur tirée par `random()` (un double), affichée comme Python l'écrit */
  | { t: 'float'; v: Rat; bin?: boolean }
  | { t: 'bool'; v: boolean }
  | { t: 'none' }
  | { t: 'str'; v: string }
  | { t: 'list'; items: PyValue[] }
  | { t: 'tuple'; items: PyValue[] }
  | { t: 'range'; start: bigint; stop: bigint; step: bigint }
  | { t: 'func'; def: Extract<Stmt, { k: 'def' }> }
  | { t: 'builtin'; name: string }
  | { t: 'module'; name: string }
  | { t: 'method'; self: PyValue; name: string };

export const NONE: PyValue = { t: 'none' };
export const pyInt = (v: bigint | number): PyValue => ({ t: 'int', v: BigInt(v) });
export const pyFloat = (v: Rat): PyValue => ({ t: 'float', v });
export const pyBool = (v: boolean): PyValue => ({ t: 'bool', v });

export const isNumeric = (x: PyValue): boolean =>
  x.t === 'int' || x.t === 'float' || x.t === 'bool';

/** valeur numérique exacte (un booléen vaut 0 ou 1, comme en Python) */
export function numOf(x: PyValue): Rat | null {
  if (x.t === 'int') return Q.rat(x.v);
  if (x.t === 'float') return x.v;
  if (x.t === 'bool') return x.v ? Q.ONE : Q.ZERO;
  return null;
}

/** égalité Python (« == ») */
export function pyEq(a: PyValue, b: PyValue): boolean {
  const x = numOf(a);
  const y = numOf(b);
  if (x && y) return Q.eq(x, y);
  if (a.t === 'str' && b.t === 'str') return a.v === b.v;
  if (a.t === 'none' && b.t === 'none') return true;
  if ((a.t === 'list' && b.t === 'list') || (a.t === 'tuple' && b.t === 'tuple'))
    return a.items.length === b.items.length && a.items.every((v, i) => pyEq(v, b.items[i]!));
  if (a.t === 'range' && b.t === 'range')
    return a.start === b.start && a.stop === b.stop && a.step === b.step;
  return false;
}

/** copie profonde (une liste mutée plus tard ne change pas un instantané) */
export function clone(x: PyValue): PyValue {
  if (x.t === 'list') return { t: 'list', items: x.items.map(clone) };
  if (x.t === 'tuple') return { t: 'tuple', items: x.items.map(clone) };
  return x;
}

/** le rationnel a-t-il une écriture décimale finie ? (dénominateur 2^a 5^b) */
export function isFiniteDecimal(q: Rat): boolean {
  let d = q.d;
  while (d % 2n === 0n) d /= 2n;
  while (d % 5n === 0n) d /= 5n;
  return d === 1n;
}

/** écriture décimale exacte d'un rationnel décimal, virgule française : « 1,5 », « −0,25 » */
export function decimalText(q: Rat, sep = ','): string {
  const neg = q.n < 0n;
  const n = neg ? -q.n : q.n;
  let places = 0;
  let scale = 1n;
  while ((n * scale) % q.d !== 0n) {
    scale *= 10n;
    places++;
    if (places > 40) break;
  }
  const digits = ((n * scale) / q.d).toString().padStart(places + 1, '0');
  const int = digits.slice(0, digits.length - places);
  const frac = places ? digits.slice(digits.length - places) : '';
  return (neg ? '−' : '') + int + (frac ? sep + frac : '');
}

/** nombre de chiffres après la virgule d'un décimal fini */
function decimalPlaces(q: Rat): number {
  let d = q.d;
  let twos = 0;
  let fives = 0;
  while (d % 2n === 0n) {
    d /= 2n;
    twos++;
  }
  while (d % 5n === 0n) {
    d /= 5n;
    fives++;
  }
  return Math.max(twos, fives);
}

/**
 * Écriture la plus courte du double le plus proche (comme `repr` en Python) : un
 * nombre tiré par `random()`, ou un résultat trop long pour l'écrire en entier.
 */
function doubleText(q: Rat, sep: string, python: boolean): string {
  const v = Number(q.n) / Number(q.d);
  if (!Number.isFinite(v)) return approxText(q, sep);
  let t = String(v);
  if (python && Number.isInteger(v) && !/e/.test(t)) t += '.0';
  return (v < 0 ? '−' + t.slice(1) : t).replace('.', sep);
}

/** valeur arrondie (affichage) d'un rationnel non décimal : 6 chiffres après la virgule */
function approxText(q: Rat, sep: string): string {
  const scaled = Q.rat(q.n * 1_000_000n, q.d);
  const r = (scaled.n * 2n + (scaled.n < 0n ? -scaled.d : scaled.d)) / (2n * scaled.d);
  return decimalText(Q.rat(r, 1_000_000n), sep);
}

/**
 * Écriture d'une valeur comme l'élève l'écrit (virgule française, signe −) : « 12 »,
 * « 1,5 », « True », « [3, 8, 1, 9] ». `python` : écriture du langage (point décimal, -).
 */
export function formatValue(x: PyValue, python = false): string {
  const sep = python ? '.' : ',';
  const minus = (s: string) => (python ? s.replace(/^−/, '-') : s);
  switch (x.t) {
    case 'int':
      return minus(x.v < 0n ? '−' + (-x.v).toString() : x.v.toString());
    case 'float': {
      // un tirage de random(), ou (écriture Python) un décimal trop long : le double de Python
      if (x.bin || (python && (!isFiniteDecimal(x.v) || decimalPlaces(x.v) > 17)))
        return minus(doubleText(x.v, sep, python));
      const t = minus(isFiniteDecimal(x.v) ? decimalText(x.v, sep) : approxText(x.v, sep));
      // Python écrit un flottant entier « 800.0 »
      return python && x.v.d === 1n ? t + '.0' : t;
    }
    case 'bool':
      return x.v ? 'True' : 'False';
    case 'none':
      return 'None';
    case 'str':
      return python ? JSON.stringify(x.v) : x.v;
    case 'list':
      return '[' + x.items.map((v) => formatValue(v, python)).join(', ') + ']';
    case 'tuple':
      return (
        '(' +
        x.items.map((v) => formatValue(v, python)).join(', ') +
        (x.items.length === 1 ? ',)' : ')')
      );
    case 'range':
      return `range(${x.start}, ${x.stop}${x.step === 1n ? '' : ', ' + x.step})`;
    case 'func':
      return `<fonction ${x.def.name}>`;
    case 'builtin':
      return `<fonction ${x.name}>`;
    case 'module':
      return `<module ${x.name}>`;
    case 'method':
      return `<méthode ${x.name}>`;
  }
}

/** nom du type, pour les messages */
export function typeName(x: PyValue): string {
  switch (x.t) {
    case 'int':
      return 'un entier';
    case 'float':
      return 'un nombre décimal';
    case 'bool':
      return 'un booléen';
    case 'none':
      return 'None';
    case 'str':
      return 'une chaîne';
    case 'list':
      return 'une liste';
    case 'tuple':
      return 'un tuple';
    case 'range':
      return 'un range';
    default:
      return 'une fonction';
  }
}
